import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, Check, CreditCard } from "lucide-react";

const FEATURE_COPY = {
  tutor_questions: "AI Tutor questions",
  worksheets: "Worksheets",
  quizzes: "Quizzes",
  tests: "Tests (Premium feature)",
  study_plans: "Study Plans (Premium feature)",
  language: "Language exercises",
};

/**
 * Mounts once at the app root. Listens for the global `tutor:dailyCap` event
 * fired by our axios interceptor when the backend returns 402 DAILY_CAP_REACHED
 * or PREMIUM_ONLY:<feature>. Shows a friendly upgrade prompt.
 */
export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("");
  const [premiumOnly, setPremiumOnly] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setFeature(e.detail?.feature || "this");
      setPremiumOnly(!!e.detail?.premiumOnly);
      setOpen(true);
    };
    window.addEventListener("tutor:dailyCap", handler);
    return () => window.removeEventListener("tutor:dailyCap", handler);
  }, []);

  if (!open) return null;
  const label = FEATURE_COPY[feature] || "this feature";

  return (
    <div data-testid="upgrade-modal" role="dialog" aria-modal="true"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 relative shadow-2xl">
        <button data-testid="upgrade-modal-close" onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-[#4B5563] hover:text-[#111827]">
          <X className="w-5 h-5" />
        </button>
        <div className="text-5xl">{premiumOnly ? "✨" : "⏰"}</div>
        <h2 className="font-display font-black text-2xl mt-3">
          {premiumOnly
            ? `${label.replace(" (Premium feature)", "")} is a Premium feature`
            : "Your daily free limit has been reached."}
        </h2>
        <p className="text-sm text-[#4B5563] mt-2">
          {premiumOnly
            ? `Upgrade to Premium Unlimited to unlock ${label.toLowerCase().replace(" (premium feature)", "")} — plus everything else, with no daily caps.`
            : `You've used today's free ${label.toLowerCase()}. Limits reset at midnight Eastern — or upgrade for unlimited access.`}
        </p>

        <ul className="mt-4 text-sm space-y-1.5">
          {[
            "Unlimited AI Tutor",
            "Unlimited Worksheets, Quizzes & Tests",
            "Unlimited English & French Practice",
            "Personalized Study Plans",
            "Priority processing",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#064E3B] mt-0.5 flex-shrink-0" />{f}</li>
          ))}
        </ul>

        <div className="mt-5 flex items-baseline gap-2">
          <span className="font-display font-black text-3xl">$9.99</span>
          <span className="text-sm text-[#4B5563]">/month</span>
          <span className="text-sm text-[#4B5563] line-through ml-1">$19.99</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#D92D20] ml-1">Launch offer</span>
        </div>

        <div className="mt-5 flex gap-2 flex-wrap">
          <Link to="/tutor/pricing" data-testid="upgrade-modal-cta" onClick={() => setOpen(false)}
            className="btn-primary inline-flex items-center gap-2 flex-1 justify-center">
            <CreditCard className="w-4 h-4" /> Upgrade Now
          </Link>
          <button data-testid="upgrade-modal-later" onClick={() => setOpen(false)}
            className="btn-outline inline-flex items-center gap-1">
            Maybe later
          </button>
        </div>
        {!premiumOnly && (
          <p className="text-[11px] text-[#4B5563] text-center mt-3">
            <Sparkles className="w-3 h-3 inline" /> Free limits reset at midnight Eastern.
          </p>
        )}
      </div>
    </div>
  );
}
